/**
 * Service class to handle deletion of objects and related operations.
 */
class DeleteObjectHelperService {
    /**
     * Constructor for DeleteObjectHelperService.
     * @param {Object} $uibModal - Angular UI Bootstrap modal service.
     * @param {Object} TaskDataService - Service to handle task data operations.
     * @param {Object} $routeParams - Angular service to retrieve route parameters.
     * @param {Object} $signals - Angular service to handle signals.
     */
    constructor($uibModal, TaskDataService, $routeParams, $signals) {
        if (!isCMS) {
            return false;
        }
        this.$uibModal = $uibModal;
        this.TaskDataService = TaskDataService;
        this.$routeParams = $routeParams;
        this.$signals = $signals;
        this._taskContext = TaskDataService.fetchContext();
    }

    /**
     * Retrieves the object key by its UUID from the content object.
     * @param {Object} contentObj - The content object containing the objects.
     * @param {string} objectUUID - The UUID of the object to find.
     * @returns {string|null} - The key of the object or null if not found.
     */
    getObjectKeyByUUID(contentObj, objectUUID) {
        let cancelForeach = false;
        let objectKey = null;
        angular.forEach(contentObj.data.objects, function (objectData, key) {
            if (cancelForeach) {
                return;
            }

            if (objectUUID === objectData.uuid && !cancelForeach) {
                cancelForeach = true;
                objectKey = key;
            }
        });
        return objectKey;
    }

    /**
     * Deletes an object by its UUID.
     * @param {string} objUUID - The UUID of the object to delete.
     */
    deleteObjectByUUID(objUUID) {
        let objectEntry = this._taskContext.read(objUUID);
        objectEntry.flagAsDeleted();
        objectEntry.flagAsDirty();

        if (angular.isFunction(objectEntry.getObjectType) && objectEntry.getObjectType() === 'universalcanvas') {
            _.filter(this._taskContext.findInstancesByType('universalCanvasPalette'), (item) => {
                let result = item.getName().indexOf(objUUID) !== -1;
                if (result) {
                    this.deleteObjectByUUID(item.getUUID());
                }
                return result;
            });
        }
    }

    /**
     * Deletes an object from the content object by its UUID.
     * @param {Object} contentObj - The content object containing the objects.
     * @param {string} objectUUID - The UUID of the object to delete.
     */
    deleteObject(contentObj, objectUUID) {
        contentObj.flagAsDirty();

        let objectKey = this.getObjectKeyByUUID(contentObj, objectUUID);
        let objUUID = contentObj.data.objects[objectKey].uuid;

        this.deleteObjectByUUID(objUUID);

        contentObj.data.objects.splice(objectKey, 1);
    }

    /**
     * Deletes objects by entry.
     * @param {Object} entry - The entry containing the objects to delete.
     */
    deleteObjectsByEntry(entry) {
        let _contentObj = entry;
        angular.forEach(_contentObj.getObjects(), (objectUUID, objKey) => {
            this.deleteObjectByUUID(objectUUID);
        });
    }

    /**
     * Deletes a subcontainer by entry.
     * @param {Object} containerEntry - The container entry to delete.
     */
    deleteSubcontainerByEntry(containerEntry) {
        containerEntry.flagAsDeleted();
        containerEntry.flagAsDirty();

        this.deleteObjectsByEntry(containerEntry);
    }

    /**
     * Deletes subcontainers.
     * @param {Array} subcontainer - The subcontainer to delete.
     */
    deleteSubcontainer(subcontainer) {
        angular.forEach(subcontainer, (container, objKey) => {
            let _containerEntry = this._taskContext.read(container.content);
            _containerEntry.flagAsDeleted();
            _containerEntry.flagAsDirty();

            this.deleteObjectsByEntry(_containerEntry);
        });
    }

    /**
     * Deletes a container.
     * @param {Object} layoutEntry - The layout entry.
     * @param {Array} rows - The rows containing the container.
     * @param {number} rowKey - The key of the row.
     * @param {number} colKey - The key of the column.
     * @param {number} containerKey - The key of the container.
     * @param {Object} containerEntry - The container entry to delete.
     */
    deleteContainer(layoutEntry, rows, rowKey, colKey, containerKey, containerEntry) {
        containerEntry.flagAsDeleted();
        containerEntry.flagAsDirty();
        if (angular.isFunction(containerEntry.getQuestionPart)) {
            let question_part = containerEntry.getQuestionPart();
            if (question_part !== null) {
                question_part.flagAsDirty();
            }
        }

        if (containerEntry.isShowTabs()) {
            let tabs = containerEntry.getTabs();
            this.deleteSubcontainer(tabs);
        } else if (containerEntry.isShowCarousels()) {
            let carousels = containerEntry.getCarousels();
            this.deleteSubcontainer(carousels);
        } else if (containerEntry.isShowIaaContainer()) {
            let iaaContainer = containerEntry.getIaaContainer();
            this.deleteSubcontainer(iaaContainer);
        }

        this.deleteObjectsByEntry(containerEntry);

        rows[rowKey].cols[colKey].list.splice(containerKey, 1);

        if (layoutEntry !== null) {
            this.$signals.emit("rendering_rows", rows);
            layoutEntry.flagAsDirty();
        }
    }

    /**
     * Deletes containers.
     * @param {Array} rows - The rows containing the containers.
     * @param {number} rowKey - The key of the row.
     * @param {number} colKey - The key of the column.
     */
    deleteContainers(rows, rowKey, colKey) {
        let containerUUID = rows[rowKey].cols[colKey].list[0].content;
        let _containerEntry = this._taskContext.read(containerUUID);
        this.deleteContainer(null, rows, rowKey, colKey, 0, _containerEntry);

        if (rows[rowKey].cols[colKey].list.length !== 0) {
            this.deleteContainers(rows, rowKey, colKey);
        }
    }

    /**
     * Deletes a column.
     * @param {Object} layoutEntry - The layout entry.
     * @param {Array} rows - The rows containing the column.
     * @param {number} rowKey - The key of the row.
     * @param {number} colKey - The key of the column.
     */
    deleteCol(layoutEntry, rows, rowKey, colKey) {
        if (typeof rows[rowKey].cols[colKey] !== "undefined" && rows[rowKey].cols[colKey].list.length > 0) {
            this.deleteContainers(rows, rowKey, colKey);
        }

        rows[rowKey].cols.splice(colKey, 1);
        if (layoutEntry !== null) {
            this.$signals.emit("rendering_rows", rows);
            layoutEntry.flagAsDirty();
        }
    }

    /**
     * Deletes columns.
     * @param {Array} rows - The rows containing the columns.
     * @param {number} rowKey - The key of the row.
     */
    deleteCols(rows, rowKey) {
        this.deleteCol(null, rows, rowKey, 0);

        if (rows[rowKey].cols.length !== 0) {
            this.deleteCols(rows, rowKey);
        }
    }

    /**
     * Deletes a row.
     * @param {Object} layoutEntry - The layout entry.
     * @param {Array} rows - The rows.
     * @param {number} rowKey - The key of the row.
     */
    deleteRow(layoutEntry, rows, rowKey) {
        if (rows.length > 0) {
            this.deleteCols(rows, rowKey);
        }

        rows.splice(rowKey, 1);
        if (layoutEntry !== null) {
            this.$signals.emit("rendering_rows", rows);
            layoutEntry.flagAsDirty();
        }
    }

    /**
     * Deletes rows.
     * @param {Object} layoutEntry - The layout entry.
     * @param {Array} rows - The rows.
     */
    deleteRows(layoutEntry, rows) {
        this.deleteRow(null, rows, 0);

        if (rows.length !== 0) {
            this.deleteRows(null, rows);
        }

        if (layoutEntry !== null) {
            layoutEntry.flagAsDirty();
            layoutEntry.flagAsDeleted();
        }
    }

    /**
     * Deletes a criterion mark.
     * @param {Object} entry - The entry containing the criterion mark.
     */
    deleteCriterionMark(entry) {
        entry.flagAsDeleted();
        entry.flagAsDirty();
    }

    /**
     * Deletes criterion marks.
     * @param {Object} entry - The entry containing the criterion marks.
     */
    deleteCriterionMarks(entry) {
        angular.forEach(entry.getCriterionMarks(), (criterionMarks, key) => {
            this.deleteCriterionMark(criterionMarks);
        });
    }

    /**
     * Deletes a question part.
     * @param {Object} entry - The entry containing the question part.
     */
    deleteQuestionPart(entry) {
        entry.flagAsDeleted();
        entry.flagAsDirty();
        this.deleteCriterionMarks(entry);
    }

    /**
     * Deletes question parts.
     * @param {Array} questionParts - The question parts to delete.
     */
    deleteQuestionParts(questionParts) {
        angular.forEach(questionParts, (questionPart, key) => {
            this.deleteQuestionPart(questionPart);
        });
    }
}

// Add explicit dependency injection
DeleteObjectHelperService.$inject = ['$uibModal', 'TaskDataService', '$routeParams', '$signals'];

export default DeleteObjectHelperService;